/**
 * Streamdown Math
 *
 * Shared KaTeX plugin config for Streamdown surfaces.
 *
 * Primary responsibilities:
 * - Keep math options consistent across chat surfaces
 * - Disable single-dollar math to avoid currency false positives
 * - Keep `\\[` / `\\(` bracket math enabled for assistant output
 *
 * Dependencies:
 * - @streamdown/math
 * - remark-math
 * - rehype-katex
 */

import { createMathPlugin, type MathPlugin } from '@streamdown/math'
import rehypeKatex from 'rehype-katex'
import remarkMath from 'remark-math'
import type { PluginConfig } from 'streamdown'

const baseMathPlugin = createMathPlugin({
  singleDollarTextMath: false
})

/** Chat-tuned math plugin (bracket math + non-throwing KaTeX). */
export const streamdownMathPlugin: MathPlugin = {
  ...baseMathPlugin,
  remarkPlugin: [
    remarkMath,
    {
      singleDollarTextMath: false,
      doubleBackslashBracketsMath: true
    }
  ],
  rehypePlugin: [
    rehypeKatex,
    {
      // KaTeX never throws into React for bad/incomplete TeX — unlike MathJax,
      // which crashes the tree on unmapped unicode (`range[4]` on null).
      throwOnError: false,
      strict: 'ignore',
      output: 'htmlAndMathml'
    }
  ]
}

export const streamdownMathPlugins = {
  math: streamdownMathPlugin
} satisfies PluginConfig
